import {
  AccountInfo,
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import {
  MergedRecorder,
  MergedRecorderListResponse,
  Recorder,
  TransferRecorderTarget,
} from "api/interfaces/recorderInterface.interface";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import * as s from "pages/recorders/RecordersStyled";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  BasePagination,
  LEVEL_TYPE,
  SORT_DIRECTION,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import * as mediaQuery from "components/MediaQuery";
import { convertType, changeLevelFromABBToABB } from "utils/functions";
import { AlertRecorerTypeFilter } from "utils/AlertUtil";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import Type from "components/atoms/text/labels/Type";
import { TableColumnsType } from "antd";
import { changeSortOrderAntd } from "components/atoms/table/AntdTable";
import TableTitle from "components/blocks/table/TableTitle";
import { ExpandableConfig, TableRowSelection } from "antd/es/table/interface";
import RecorderListExpand from "pages/recorders/recorders/list/RecorderListExpand";
import AccountExpandContainer from "pages/account/AccountExpandContainer";
import StagingAssignPresenter from "./StagingAssignPresenter";
import {
  DataRow,
  mergedRecorderToDataRow,
} from "pages/recorders/recorders/list/RecorderListContainer";
import { checkIsSuspended } from "utils/AccountUtil";
import SuspendedLabel from "components/atoms/text/labels/SuspendedLabel";
import AccountId from "components/atoms/text/labels/AccountId";
import { suspendOptions } from "utils/options";
import { useProSidebar } from "react-pro-sidebar";
import useApiError from "hook/useApiError";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { getRecorderList, postTransferRecorder } from "api/recorderAPI";
import { useInfiniteQuery } from "react-query";
import { getAccountTreeList } from "api/accountAPI";
import { GetAccountListResponse } from "api/interfaces/accountInterface.interface";
import useStatusMutation from "hook/useStatusMutation";
import { StagingPageOutletContext } from "../StagingPage";
import { StatusType } from "components/blocks/status/Status";

interface Props {}

const initialQuery: BasePagination = {
  ...baseQueryInfo,
  sortType: "mergedSystemName",
};

export default function StagingRecorderContainer(props: Props) {
  const {
    onHeaderCell,
    onClickFilterTitle,
    setButtons,
    onClickSearch,
    setClassName,
    setNoSearch,
    setTitleComponent,
    setCenterChildren,
  } = useOutletContext<StagingPageOutletContext>();
  const [page, setPage] = useState<number>(0);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [status, setStatus] = useState<StatusType>({
    totalTitle: "Total",
    totalValue: 0,
    items: [],
    isMobileShow: {
      totalTitle: true,
      totalValue: true,
      items: true,
    },
  });

  const { menuNavigation } = useMenu({});

  const [searchParams] = useSearchParams();

  const buildRecorderQueryFromSearch = useMemo(() => {
    const get = (key: string) => searchParams.get(key);
    return {
      ...initialQuery,
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
      ...((get("status") || undefined) && { status: get("status") }),
    };
  }, [searchParams]);

  const buildAccountQueryFromSearch = useMemo(() => {
    const get = (key: string) => searchParams.get(key);
    return {
      ...baseQueryInfo,
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
      ...((get("status") || undefined) && { status: get("status") }),
    };
  }, [searchParams]);

  const recorderFilterQuery = useMemo(() => {
    const {
      pageNum: _pageNum,
      total: _total,
      ...rest
    } = buildRecorderQueryFromSearch;
    return rest;
  }, [buildRecorderQueryFromSearch]);

  const accountFilterQuery = useMemo(() => {
    const {
      pageNum: _pageNum,
      total: _total,
      ...rest
    } = buildAccountQueryFromSearch;
    return rest;
  }, [buildAccountQueryFromSearch]);

  const recorderStableKey = useQueryKey(recorderFilterQuery);
  const accountStableKey = useQueryKey(accountFilterQuery);

  const [selectedSystemKeys, setSelectedSystemKeys] = useState<React.Key[]>([]);
  const [selectedRecorderRows, setSelectedRecorderRows] = useState<DataRow[]>(
    []
  );
  /** Step 1: single organization */
  const [selectedAccountRow, setSelectedAccountRow] = useState<
    AccountInfo | undefined
  >();

  const { collapsed } = useProSidebar();

  const [expandedRecorderKeys, setExpandedRecorderKeys] = useState<string[]>(
    []
  );
  const [expandedAccountKeys, setExpandedAccountKeys] = useState<string[]>([]);
  const isMobile = mediaQuery.useIsMobile();
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({});
  const { lang, intlString } = useApiError();
  const { notifySuccess, handleErrorNotify } = useApiError();

  const isAccountRowExpandable = useCallback(
    (record: AccountInfo) =>
      record.hasAccounts > 0 && record.level !== LEVEL_TYPE.EU,
    []
  );

  const getNextPageParamFromPage = (
    lastPage:
      | { page?: { total: number; pageNum: number; pageLimit: number } }
      | undefined
  ) => {
    if (!lastPage?.page) return undefined;
    const { total, pageNum, pageLimit } = lastPage.page;
    const nextPage = pageNum + 1;
    return nextPage * pageLimit < total ? nextPage : undefined;
  };

  const {
    refetch: refetchRecorder,
    data: recorderData,
    isLoading: isLoadingRecorder,
    isFetchingNextPage: isFetchingNextRecorderPage,
    hasNextPage: hasNextRecorderPage,
    fetchNextPage: fetchNextRecorderPage,
  } = useInfiniteQuery(
    ["stagingAssignRecorder", recorderStableKey, selectedAccount.accountId],
    ({ pageParam = 0 }) =>
      getRecorderList({
        payload: {
          ...baseQueryInfo,
          ...recorderFilterQuery,
          pageNum: pageParam,
          sortType:
            recorderFilterQuery.sortType === "name"
              ? "mergedSystemName"
              : recorderFilterQuery.sortType,
        },
        accountId: selectedAccount.accountId,
      }),
    {
      enabled: (page === 0 || page === 2) && !!selectedAccount.accountId,
      refetchOnWindowFocus: false,
      retry: 0,
      getNextPageParam: (lastPage: MergedRecorderListResponse | undefined) => {
        if (lastPage?.error !== 0 || !lastPage?.result) return undefined;
        return getNextPageParamFromPage(lastPage);
      },
      onSuccess: (data: { pages: MergedRecorderListResponse[] }) => {
        const lastPage = data.pages[data.pages.length - 1];
        if (lastPage?.error !== 0 || !lastPage?.page) return;
        setStatus((info) => ({
          ...info,
          totalValue: lastPage.page.total,
        }));
      },
    }
  );

  const {
    data: accountData,
    refetch: refetchAccount,
    isLoading: isLoadingAccount,
    isFetchingNextPage: isFetchingNextAccountPage,
    hasNextPage: hasNextAccountPage,
    fetchNextPage: fetchNextAccountPage,
  } = useInfiniteQuery(
    ["stagingAssignAccount", accountStableKey, selectedAccount.accountId],
    ({ pageParam = 0 }) =>
      getAccountTreeList({
        payload: {
          ...baseQueryInfo,
          ...accountFilterQuery,
          pageNum: pageParam,
          sortType:
            accountFilterQuery.sortType === "mergedSystemName"
              ? "name"
              : accountFilterQuery.sortType,
        },
        accountId: selectedAccount.accountId,
        isSelf: false,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: page === 1 && !!selectedAccount.accountId,
      getNextPageParam: (lastPage: GetAccountListResponse | undefined) =>
        getNextPageParamFromPage(lastPage),
      onSuccess: (data: { pages: GetAccountListResponse[] }) => {
        const lastPage = data.pages[data.pages.length - 1];
        if (!lastPage?.page) return;
        setStatus((info) => ({
          ...info,
          totalValue: lastPage.page.total,
        }));
      },
      onError: () => {
        setStatus({
          totalTitle: "Total ",
          totalValue: 0,
        });
      },
    }
  );

  const recorderQueryInfo = useMemo<BasePagination>(() => {
    const lastPage = recorderData?.pages?.[recorderData.pages.length - 1];
    return {
      ...recorderFilterQuery,
      pageNum: 0,
      pageLimit: lastPage?.page?.pageLimit ?? baseQueryInfo.pageLimit,
      total: lastPage?.page?.total ?? 0,
    };
  }, [recorderFilterQuery, recorderData]);

  const accountQueryInfo = useMemo<BasePagination>(() => {
    const lastPage = accountData?.pages?.[accountData.pages.length - 1];
    return {
      ...accountFilterQuery,
      pageNum: 0,
      pageLimit: lastPage?.page?.pageLimit ?? baseQueryInfo.pageLimit,
      total: lastPage?.page?.total ?? 0,
    };
  }, [accountFilterQuery, accountData]);

  const loadMoreRecorder = useCallback(() => {
    if (
      !hasNextRecorderPage ||
      isFetchingNextRecorderPage ||
      isLoadingRecorder
    ) {
      return;
    }
    fetchNextRecorderPage();
  }, [
    hasNextRecorderPage,
    isFetchingNextRecorderPage,
    isLoadingRecorder,
    fetchNextRecorderPage,
  ]);

  const loadMoreAccount = useCallback(() => {
    if (!hasNextAccountPage || isFetchingNextAccountPage || isLoadingAccount) {
      return;
    }
    fetchNextAccountPage();
  }, [
    hasNextAccountPage,
    isFetchingNextAccountPage,
    isLoadingAccount,
    fetchNextAccountPage,
  ]);

  useStompDebouncedRefetch({
    stompKey: [
      "organizationListUpdated",
      "loginAccessUpdated",
    ] as StompDateSignalKey[],
    refetch: refetchAccount,
  });
  const columns = useMemo(() => {
    if (page === 1) {
      return [
        {
          key: "accountNumber",
          dataIndex: "accountNumber",
          title: "ID",
          render: (value, row) => (
            <AccountId
              accountId={value}
              level={changeLevelFromABBToABB(row) as LEVEL_TYPE}
            />
          ),
          width: 95,
          sorter: true,
          sortOrder: changeSortOrderAntd(accountQueryInfo, "accountNumber"),
          onHeaderCell: onHeaderCell,
          hidden: isMobile,
        },
        {
          key: "name",
          title: "Name",
          render: (value, row) => (
            <>
              {isMobile && (
                <AccountId
                  accountId={row.accountNumber}
                  level={changeLevelFromABBToABB(row) as LEVEL_TYPE}
                />
              )}
              <span>{row.name}</span>
            </>
          ),
          sorter: true,
          sortOrder: changeSortOrderAntd(accountQueryInfo, "name"),
          defaultSortOrder: "ascend",
          onHeaderCell: onHeaderCell,
          ellipsis: true,
        },
        {
          title: (
            <TableColumnFilterTitle
              title="Status"
              filters={suspendOptions.map((opt) => ({
                label: opt.label,
                value: opt.label.toLowerCase(),
              }))}
              onClick={(value: string | undefined) => {
                onClickFilterTitle(value, "status");
              }}
            />
          ),
          render: (value, row) => {
            const suspendedStatus = checkIsSuspended({
              ...row,
              accountLevel: row.level as LEVEL_TYPE,
            });
            return <SuspendedLabel status={suspendedStatus} />;
          },
          width: 80,
          hidden: isMobile,
        },
      ] as TableColumnsType<AccountInfo>;
    }
    if (page === 2) {
      return [
        {
          key: "recorderType",
          title: (
            <TableColumnFilterTitle
              title="Type"
              filters={AlertRecorerTypeFilter}
              onClick={(value: string | undefined) => {
                onClickFilterTitle(value, "type");
              }}
            />
          ),
          width: isMobile ? 90 : 130,
          render: (value, row) => (
            <Type
              type={convertType(row)}
              licenseType={row.licenseType}
              isCloudCam={row.isCloudCam}
            />
          ),
        },
        {
          title: "Title",
          sorter: true,
          key: "mergedSystemName",
          defaultSortOrder: "ascend",
          sortOrder: changeSortOrderAntd(recorderQueryInfo, "mergedSystemName"),
          onHeaderCell: onHeaderCell,
          render: (value, row) =>
            row.recorderCount !== undefined && row.recorderCount > 0 ? (
              <TableTitle row={row} />
            ) : (
              <></>
            ),
          ellipsis: true,
        },
        {
          title: "Cameras",
          render: (value, row) =>
            row.cameras > 0 ? (
              <s.RowLink
                onClick={() => {
                  if (!selectedAccount.isEnabled) return;
                  menuNavigation({
                    to: `/recorders/list/cameras/${row.mergedSystemId}${
                      row.recorderId ? `/${row.recorderId}` : ""
                    }`,
                  });
                }}
                disabled={!selectedAccount.isEnabled}
              >
                {row.cameras}
              </s.RowLink>
            ) : (
              row.cameras
            ),
          align: "center",
          width: isMobile ? 60 : 100,
        },
      ] as TableColumnsType<DataRow>;
    }
    return [
      {
        key: "type",
        title: (
          <TableColumnFilterTitle
            title="Type"
            filters={AlertRecorerTypeFilter}
            onClick={(value: string | undefined) => {
              onClickFilterTitle(value, "type");
            }}
          />
        ),
        width: isMobile ? 90 : 110,
        render: (value, row) => (
          <Type
            type={convertType(row)}
            licenseType={row.licenseType}
            isCloudCam={row.isCloudCam}
          />
        ),
      },
      {
        title: "Title",
        sorter: true,
        key: "mergedSystemName",
        defaultSortOrder: "ascend",
        sortOrder: changeSortOrderAntd(recorderQueryInfo, "mergedSystemName"),
        onHeaderCell: onHeaderCell,
        render: (value, row) =>
          row.recorderCount !== undefined && row.recorderCount > 0 ? (
            <TableTitle row={row} />
          ) : (
            <></>
          ),
        ellipsis: true,
      },
      {
        title: "Cameras",
        render: (value, row) =>
          row.cameras > 0 ? (
            <s.RowLink
              onClick={() => {
                if (!selectedAccount.isEnabled) return;
                menuNavigation({
                  to: `/recorders/list/cameras/${row.mergedSystemId}${
                    row.recorderId ? `/${row.recorderId}` : ""
                  }`,
                });
              }}
              disabled={!selectedAccount.isEnabled}
            >
              {row.cameras}
            </s.RowLink>
          ) : (
            row.cameras
          ),
        align: "center",
        width: isMobile ? 60 : 100,
      },
    ] as TableColumnsType<DataRow>;
  }, [
    accountQueryInfo,
    isMobile,
    menuNavigation,
    onClickFilterTitle,
    onHeaderCell,
    page,
    recorderQueryInfo,
    selectedAccount,
  ]) as TableColumnsType<DataRow | AccountInfo>;

  const allRecorderRows = useMemo<DataRow[]>(() => {
    return (recorderData?.pages ?? [])
      .flatMap(
        (p: MergedRecorderListResponse) => p.result?.mergedSystemList ?? []
      )
      .map((rec: MergedRecorder) => ({
        ...mergedRecorderToDataRow(rec),
        key: rec.mergedSystemId,
      }));
  }, [recorderData?.pages]);

  const accountRows = useMemo<AccountInfo[]>(() => {
    return (accountData?.pages ?? [])
      .flatMap((p: GetAccountListResponse) => p.result ?? [])
      .map((account: AccountInfo) => ({
        ...account,
        key: account.accountId,
      }));
  }, [accountData?.pages]);

  const data = useMemo<any[]>(() => {
    if (page === 0) return allRecorderRows;
    if (page === 1) return accountRows;
    if (page === 2) {
      if (selectedRecorderRows.length > 0) {
        return selectedRecorderRows;
      }
      const selectedKeySet = new Set(
        selectedSystemKeys.map((key) => String(key))
      );
      return allRecorderRows.filter((row) =>
        selectedKeySet.has(String(row.mergedSystemId))
      );
    }
    return [];
  }, [
    page,
    allRecorderRows,
    accountRows,
    selectedSystemKeys,
    selectedRecorderRows,
  ]);

  const loadMore = page === 0 ? loadMoreRecorder : loadMoreAccount;
  const hasMore =
    page === 0
      ? !!hasNextRecorderPage
      : page === 1
      ? !!hasNextAccountPage
      : false;
  const isInitialLoading =
    page === 0 ? isLoadingRecorder : page === 1 ? isLoadingAccount : false;

  const rowSelection = useMemo<TableRowSelection<any> | undefined>(() => {
    if (page === 0) {
      return {
        type: "checkbox",
        selectedRowKeys: selectedSystemKeys,
        onChange: (keys, rows) => {
          setSelectedSystemKeys(keys);
          setSelectedRecorderRows((rows ?? []) as DataRow[]);
        },
        getCheckboxProps: () => ({ onClick: (e) => e.stopPropagation() }),
      };
    }
    if (page === 1) {
      return {
        type: "radio",
        selectedRowKeys: selectedAccountRow
          ? [selectedAccountRow.accountId]
          : [],
        onChange: (_keys, rows) => {
          const row = rows[0] as AccountInfo | undefined;
          if (row?.level === LEVEL_TYPE.EU) {
            setSelectedAccountRow(row);
          }
        },
        getCheckboxProps: (record: AccountInfo) => ({
          disabled: record.level !== LEVEL_TYPE.EU,
        }),
      };
    }
    return undefined;
  }, [page, selectedAccountRow, selectedSystemKeys]);

  const expandableProps = useMemo<
    ExpandableConfig<DataRow | AccountInfo> | undefined
  >(() => {
    return {
      expandRowByClick: false,
      expandedRowKeys:
        page === 0 || page === 2
          ? expandedRecorderKeys.map((key) => String(key))
          : expandedAccountKeys.map((key) => String(key)),
      onExpand: (expanded: boolean, record: AccountInfo | DataRow) => {
        if (page === 0 || page === 2) {
          const id = String((record as DataRow).mergedSystemId);
          setExpandedRecorderKeys((prev) =>
            expanded ? [...prev, id] : prev.filter((k) => k !== id)
          );
        } else {
          const id = String((record as AccountInfo).accountId);
          setExpandedAccountKeys((prev) =>
            expanded ? [...prev, id] : prev.filter((k) => k !== id)
          );
        }
      },
      expandedRowRender: (record: AccountInfo | DataRow) => {
        if (page === 0 || page === 2) {
          return (
            <RecorderListExpand
              mergedSystemId={(record as DataRow).mergedSystemId}
              columns={columns as TableColumnsType<Recorder>}
              queryInfo={recorderQueryInfo}
            />
          );
        }
        const account = record as AccountInfo;
        return (
          <AccountExpandContainer
            key={account.accountId}
            selectedData={account as AccountInfo & { key?: string }}
            columns={columns as TableColumnsType<AccountInfo>}
            queryInfo={accountQueryInfo}
            rowSelection={rowSelection}
            expandable={
              isAccountRowExpandable(account)
                ? (expandableProps as ExpandableConfig<AccountInfo>)
                : undefined
            }
          />
        );
      },
      rowExpandable: (record: AccountInfo | DataRow) =>
        page === 0 || page === 2
          ? ((record as DataRow).recorderCount ?? 0) > 1
          : isAccountRowExpandable(record as AccountInfo),
    };
  }, [
    recorderQueryInfo,
    expandedRecorderKeys,
    columns,
    accountQueryInfo,
    expandedAccountKeys,
    isAccountRowExpandable,
    page,
    rowSelection,
  ]);
  useStompDebouncedRefetch({
    stompKey: ["recorderListUpdated"] as StompDateSignalKey[],
    refetch: refetchRecorder,
  });

  useEffect(() => {
    setExpandedRecorderKeys([]);
    setExpandedAccountKeys([]);
  }, [page]);
  const titleComponent = useMemo(() => {
    if (page === 1) {
      return "Select Organization";
    }
    if (page === 2) {
      return "Recorders";
    }
    return "Select Recorders";
  }, [page]);
  const mutationTransferRecorder = useStatusMutation(postTransferRecorder, {
    onSuccess: () => {
      notifySuccess(lang.label.recorder.notify.transfer.success);
      menuNavigation({ to: "/maintenance/staging/history" });
    },
    onError: (err) => {
      handleErrorNotify(err, lang.label.recorder.notify.transfer.fail);
    },
  });
  const onSave = () => {
    if (!mutationTransferRecorder.idle) return;
    const allMergedSystems = (recorderData?.pages ?? []).flatMap(
      (p: MergedRecorderListResponse) => p.result?.mergedSystemList ?? []
    );
    let targets: TransferRecorderTarget[] = selectedSystemKeys.map((key) => {
      return {
        mergedSystemId: key as string,
        type: allMergedSystems.find(
          (system: MergedRecorder) => system.mergedSystemId === key
        )?.displayType,
      };
    });
    if (targets.length === 0) {
      setPage(0);
      setValidateMsg((prev) => ({
        ...prev,
        system: intlString(lang.validateMsg.userGroup.recorder),
      }));
      return;
    }
    if (!selectedAccountRow) {
      setPage(1);
      setValidateMsg((prev) => ({
        ...prev,
        account: intlString(lang.validateMsg.report.rule.emptySingleAccount),
      }));
      return;
    }
    mutationTransferRecorder.mutate({
      accountId: selectedAccount.accountId,
      payload: {
        targetAccountId: selectedAccountRow?.accountId,
        targets: targets,
      },
    });
  };
  const onChangeStep = (step: "previous" | "next" | number) => {
    setValidateMsg({});
    let to = page + 1;
    if (typeof step === "number") {
      to = step;
    }
    if (typeof step === "string") {
      if (step === "previous") {
        to = page - 1;
      }
      if (step === "next") {
        to = page + 1;
      }
    }
    const isNext = to > page;
    const isPrevious = to < page;
    if (isPrevious && page > 0) {
      setPage(to);
      return;
    }
    if (isNext && page < 2) {
      if (page === 0 && selectedSystemKeys.length === 0) {
        setValidateMsg((prev) => ({
          ...prev,
          system: intlString(lang.validateMsg.userGroup.recorder),
        }));
        return;
      }
      if (page === 1 && selectedAccountRow === undefined) {
        setValidateMsg((prev) => ({
          ...prev,
          account: intlString(lang.validateMsg.report.rule.emptySingleAccount),
        }));
        return;
      }
      if (to === 2) {
        setStatus((info) => ({
          ...info,
          totalValue: selectedSystemKeys.length,
        }));
      }
      setPage(to);
      return;
    }
    if (isNext && page === 2) {
      onSave();
      return;
    }
  };
  const onClickCancel = () => {
    menuNavigation({ to: "/maintenance/staging" });
  };
  useEffect(() => {
    setButtons(null);
    setClassName("bg-container");
    setNoSearch(true);
    setTitleComponent([
      {
        label: "Staging Recorder",
        onClick: () => menuNavigation({ to: "/maintenance/staging" }),
      },
    ]);
    setCenterChildren(null);
  }, [setButtons]);
  return (
    <StagingAssignPresenter
      columns={columns}
      data={data}
      rowKey={page === 1 ? "accountId" : "mergedSystemId"}
      rowSelection={rowSelection}
      loadMore={loadMore}
      hasMore={hasMore}
      isInitialLoading={isInitialLoading}
      expandableProps={expandableProps}
      onClickSearch={onClickSearch}
      status={status}
      titleComponent={titleComponent}
      page={page}
      isCollapsed={collapsed}
      onChangeStep={onChangeStep}
      onClickCancel={onClickCancel}
      selectedAccount={selectedAccount}
      selectedAccountRow={selectedAccountRow}
      validateMsg={validateMsg}
    />
  );
}
